import { useEffect, useState } from 'react';

import { Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { PageInfo, selectPageInfo, selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { IUser, UserProfileSearchDropdown } from 'common/components/sharedCustomComponents/userProfileSearchDropdown/UserProfileSearchDropdown';
import { WarningMessageWithCheckboxAcknowledgement } from 'common/components/sharedCustomComponents/WarningMessageWithCheckboxAcknowledgement/WarningMessageWithCheckboxAcknowledgement';
import { IDENTITY_RETRIEVAL_ERROR_MESSAGE, IDENTITY_RETRIEVAL_ERROR_TOOLTIP_MESSAGE, INACTIVE_IDENTITY_CHECKBOX_LABEL, INACTIVE_IDENTITY_WARNING_MESSAGE } from 'common/constants/inactiveIdentityCheckDisplayMessages';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserGroupsUsers, postUserGroupUserMembership } from 'common/services/api/UserGroupsUsers/userGroupsUsers.thunk';
import { fetchUserProfilesById } from 'common/services/api/UserProfiles/UserProfiles.thunk';
import { EditDrawerContentProps } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { selectFilters } from './userGroupsUsersSlice';

const constructCreateContentComponent = (
    title: string,
    pageInfo: PageInfo | undefined,
    saveButtonFunction: () => void,
    processing: boolean,
    userInfo: IUser | null,
    handleChange: (e: unknown, userInfo: IUser | null) => void,
    nameInConflict: boolean,
    inactiveIdentity: boolean,
    inactiveIdentityChecked: boolean,
    setInactiveIdentityChecked: (checked: boolean) => void,
    fetchingIdentityStatus: boolean,
    warningMessage: string,
    tooltipDescription?: string
) => (
    <EditDrawer
        title={title}
        saveButtonFunction={saveButtonFunction}
        processing={processing}
    >
        <Stack spacing={2}>
            <UserProfileSearchDropdown
                value={userInfo}
                onChange={handleChange}
                hasError={nameInConflict}
            />
            {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                >
                    This User is already linked to this User Group.
                </Box>
            }
            {fetchingIdentityStatus &&
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '1rem' }}>
                    <CircularProgress size={20} data-testid='loading'/>
                    <Typography variant="body2" color="text.secondary">
                        Checking user identity statuses...
                    </Typography>
                </Box>
            }
            <WarningMessageWithCheckboxAcknowledgement
                show={!fetchingIdentityStatus && inactiveIdentity}
                checked={inactiveIdentityChecked}
                warningMessage={warningMessage}
                checkboxLabel={INACTIVE_IDENTITY_CHECKBOX_LABEL}
                tooltipDescription={tooltipDescription}
                onCheckedChange={setInactiveIdentityChecked}
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="User Group Name"
                variant="filled"
                defaultValue={pageInfo?.name ?? ''}
                disabled
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="Tenant Name"
                variant="filled"
                defaultValue={pageInfo?.tenantName ?? ''}
                disabled
            />
        </Stack>
    </EditDrawer>
);

export function UserGroupsUsersCreateDrawerContent({
    title,
}: Readonly<EditDrawerContentProps<{ name: string }>>) {
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const { currentPage = 0, rowsPerPage = 0 } = paginationGridData ?? {};
    const filterProp = useAppSelector(selectFilters);
    const { keywords } = filterProp;
    const [ userInfo, setUserInfo ] = useState<IUser | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const [inactiveIdentity, setInactiveIdentity] = useState<boolean>(false);
    const [inactiveIdentityChecked, setInactiveIdentityChecked] = useState<boolean>(false);
    const [fetchingIdentityStatus, setFetchingIdentityStatus] = useState<boolean>(false);
    const [warningMessage, setWarningMessage] = useState<string>(INACTIVE_IDENTITY_WARNING_MESSAGE);
    const [tooltipDescription, setTooltipDescription] = useState<string | undefined>(undefined);
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const { enqueueSnackbar } = useSnackbar();
    const nameInConflict = useAppSelector(selectNameInConflict);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if(currentId === DrawerIDs.User){
            setUserInfo(null);
            setInactiveIdentity(false);
            setInactiveIdentityChecked(false);
            setFetchingIdentityStatus(false);
            setWarningMessage(INACTIVE_IDENTITY_WARNING_MESSAGE);
            setTooltipDescription(undefined);
        }
    }, [drawerOpen, currentId]);

    const handleChange = (_e: unknown, newUserInfo: IUser | null) => {
        if(newUserInfo)
        {
            setUserInfo({
                id: newUserInfo.id,
                profile: {
                    firstName: newUserInfo.profile.firstName,
                    lastName: newUserInfo.profile.lastName,
                    principalName: newUserInfo.profile.principalName,
                    email: newUserInfo.profile.email,
                    companyKey: newUserInfo.profile.companyKey
                }
            });

            // Fetch user profiles to check activeIdentityStatus
            setInactiveIdentity(false);
            setInactiveIdentityChecked(false);
            setFetchingIdentityStatus(true);
            dispatch(fetchUserProfilesById(newUserInfo.id))
                .unwrap()
                .then((userProfiles) => {
                    const hasActiveIdentity = userProfiles?.some(
                        profile => profile.activeIdentityStatus === true
                    ) ?? false;
                    setInactiveIdentity(!hasActiveIdentity);
                    if (hasActiveIdentity) {
                        setInactiveIdentityChecked(false);
                    }
                    setWarningMessage(INACTIVE_IDENTITY_WARNING_MESSAGE);
                    setTooltipDescription(undefined);
                })
                .catch(() => {
                    // If API call fails, show error message and require acknowledgment
                    setInactiveIdentity(true);
                    setInactiveIdentityChecked(false);
                    setWarningMessage(IDENTITY_RETRIEVAL_ERROR_MESSAGE);
                    setTooltipDescription(IDENTITY_RETRIEVAL_ERROR_TOOLTIP_MESSAGE);
                })
                .finally(() => {
                    setFetchingIdentityStatus(false);
                });
        } else {
            setUserInfo(null);
            setInactiveIdentity(false);
            setInactiveIdentityChecked(false);
            setFetchingIdentityStatus(false);
            setWarningMessage(INACTIVE_IDENTITY_WARNING_MESSAGE);
            setTooltipDescription(undefined);
        }
        dispatch(setSaveButtonEnabled(!!newUserInfo));
        dispatch(setNameInConflict(false));
    };

    useEffect(() => {
        if (fetchingIdentityStatus) {
            dispatch(setSaveButtonEnabled(false));
        } else if (userInfo && inactiveIdentity && !inactiveIdentityChecked) {
            dispatch(setSaveButtonEnabled(false));
        } else if (userInfo) {
            dispatch(setSaveButtonEnabled(true));
        }
    }, [userInfo, inactiveIdentity, inactiveIdentityChecked, fetchingIdentityStatus, dispatch]);

    const userLinkPostCallback = () => {
        setProcessing(true);
        const userGroupOptions = {
            tenantId: pageInfo?.tenantId ?? '',
            userGroupId: pageInfo?.id ?? '',
            userId: userInfo?.id ?? '',
            paginationInfo: {
                skip: currentPage * rowsPerPage,
                take: rowsPerPage,
                search: keywords[0],
            },
            status: filterProp.dropdowns.status.keys
        };
        dispatch(postUserGroupUserMembership(userGroupOptions))
            .unwrap()
            .then((result) => {
                enqueueSnackbar('The User was successfully linked.', { variant: 'success' });
                dispatch(setNameInConflict(false));
                dispatch(closeDrawer());
                dispatch(setPaginationGridData({
                    rows: result.rows,
                    rowsCount: result.count,
                    currentPage: result.skipped / rowsPerPage,
                    rowsPerPage,
                }));
            })
            .catch((error) => {
                dispatch(setNameInConflict(false));
                switch (extractStatusCode(error.message)) {
                    case 404: {
                        if (currentPage === 0) {
                            enqueueSnackbar('The User was successfully linked.', { variant: 'success' });
                            dispatch(setPaginationGridData({}));
                            dispatch(closeDrawer());
                        }
                        dispatch(fetchUserGroupsUsers({
                            tenantId: pageInfo?.id ?? '',
                            usergroupId: pageInfo?.id ?? '',
                            skip: 0,
                            take: rowsPerPage,
                            search: keywords[0],
                            status: filterProp.dropdowns.status.keys
                        }))
                            .unwrap()
                            .then((result) => {
                                dispatch(setPaginationGridData({
                                    rows: result.data,
                                    rowsCount: result.count,
                                    currentPage: result.skipped / rowsPerPage,
                                    rowsPerPage,
                                }));
                            })
                            .catch(() => {
                                dispatch(setPaginationGridData({}));
                            })
                            .finally(() => {
                                enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
                                dispatch(closeDrawer());
                            });
                        break;
                    }
                    case 409: {
                        dispatch(setNameInConflict(true));
                        break;
                    }
                    default: {
                        enqueueSnackbar(
                            'There was an error while linking the User.' +
                            DEFAULT_ERROR_MESSAGE,
                            { variant: 'error' }
                        );
                        break;
                    }
                }
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    return constructCreateContentComponent(
        title,
        pageInfo,
        userLinkPostCallback,
        processing,
        userInfo,
        handleChange,
        nameInConflict ?? false,
        inactiveIdentity,
        inactiveIdentityChecked,
        setInactiveIdentityChecked,
        fetchingIdentityStatus,
        warningMessage,
        tooltipDescription
    );
}
